import {
  ConversationStatus,
  InboxEntry as SpinnerInboxEntry,
  InboxState
} from "@closerplatform/spinner-openapi";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { sum } from "lodash";
import {
  flow,
  getSnapshot,
  Instance,
  isStateTreeNode,
  SnapshotOrInstance,
  types
} from "mobx-state-tree";
import {
  Conversation,
  conversationResourceDataToSnapshot,
  IConversationSnapshotIn
} from "../base/Conversation";
import {
  callStoreGetter,
  inboxStoreGetter,
  routerStoreGetter,
  servicesGetters,
  uiStoreGetter
} from "../utils";
import {
  GlobalFilterTypes,
  IFilterAnyThreadClosed,
  IFilterAssignee,
} from "./ConcreteGlobalFilters";
import { IInboxEntry, InboxEntry } from "./InboxEntry";
import { InboxEntryReferenceType } from "./types";
import { config } from "@fidget/common/config";

export enum InboxFilterId {
  YOURS = "yours",
  FOLLOWED = "followed",
  WAITING = "waiting",
  IN_PROGRESS = "inProgress",
  CLOSED = "closed"
}

export enum InboxLoadingState {
  UNLOADED = "unloaded",
  FULL_LOADING = "full loading",
  PARTIAL_LOADING = "partial loading",
  LOADED_PARTIALLY = "loaded partially",
  LOADED = "loaded",
  ERROR = "error"
}

export enum UnreadType {
  MESSAGES = "messages",
  ENTRIES = "entries"
}

export type InboxEntryObject = SnapshotOrInstance<typeof InboxEntry>;

export const InboxFilter = types
  .model("InboxFilter", {
    // TODO: limit to InboxFilterId.values()
    // id: types.identifier<InboxFilterId>(types.refinement(types.string, (id) => Object.values(InboxFilterId).includes(id)),
    id: types.identifier,
    unreadType: types.maybe(types.enumeration(Object.values(UnreadType))),
    unreadCount: types.maybe(types.number),
    loadingState: types.optional(
      types.enumeration<InboxLoadingState>("InboxLoadingState", Object.values(InboxLoadingState)),
      InboxLoadingState.UNLOADED
    ),
    loadedEntries: types.array(InboxEntry),
    pageSize: 0, // for paging purpose, default 0: no paging is applied.
    offset: types.maybe(types.number),
    lastSelectedEntry: types.maybe(InboxEntryReferenceType)
  })
  .views(servicesGetters)
  .views(inboxStoreGetter)
  .views(callStoreGetter)
  .views(routerStoreGetter)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      /**
       * Filter's label, translated
       */
      get label() {
        const messageId = ("inbox.filters." + self.id) as any;
        return intl.formatMessage(messageId);
      },
      /**
       * Filter's placeholder text, translated
       */
      get placeholderText() {
        if (self.inboxStore.globalFilter.isAnyFilterEnabled) {
          return intl.formatMessage("inbox.placeholder.globalFilter");
        }

        const messageId = ("inbox.placeholder." + self.id) as any;
        return intl.formatMessage(messageId);
      },
      /**
       * Should the filter (re)load its entries when it's selected or initialized
       */
      get requiresReload() {
        const { UNLOADED, ERROR } = InboxLoadingState;
        return self.loadingState === UNLOADED || self.loadingState === ERROR;
      },

      /**
       * Is the filter selected as current
       */
      get isSelected(): boolean {
        return self.inboxStore && self.inboxStore.selectedFilter === self;
      },

      /**
       * Whether more entries are available but not yet loaded.
       */
      get hasMoreEntries() {
        return self.loadingState === InboxLoadingState.LOADED_PARTIALLY;
      },

      /**
       * Whether next entries are being loaded.
       */
      get isLoadingNextEntries() {
        return self.loadingState === InboxLoadingState.PARTIAL_LOADING;
      },

      /**
       * Is unread count of entries displayed
       */
      get isUnreadEntriesCountDisplayed() {
        return self.unreadType === UnreadType.ENTRIES && !!self.unreadCount;
      },

      /**
       * unread count of messages displayed
       */
      get unreadMessagesCount(): number | undefined {
        return self.unreadType === UnreadType.MESSAGES && self.unreadCount !== undefined
          ? self.unreadCount
          : undefined;
      },

      get isPermitted(): boolean {
        return views.isWholePermitted || self.uiStore.canDisplayTagGroupConversations();
      },

      get isWholePermitted(): boolean {
        if (self.id === InboxFilterId.YOURS) {
          return self.uiStore.canDisplayYoursConversations();
        }

        if (self.id === InboxFilterId.WAITING) {
          return self.uiStore.canDisplayWaitingConversations();
        }

        if (self.id === InboxFilterId.IN_PROGRESS) {
          return self.uiStore.canDisplayInProgressConversations();
        }

        if (self.id === InboxFilterId.FOLLOWED) {
          return self.uiStore.canDisplayFollowedConversations();
        }

        if (self.id === InboxFilterId.CLOSED) {
          return self.uiStore.canDisplayClosedConversations();
        }

        return false;
      }
    };

    return views;
  })
  .actions((self) => ({
    /**
     * Set unread messages count
     * @param {number|undefined} value  number of unread entries to set, will determine from loadedEntries if omitted
     */
    setUnreadCount(value: number) {
      self.unreadCount = value;
    }
  }))

  .actions((self) => {
    const getPagedConversations = async (): Promise<InboxState> => {
      const snoozedFilterEnabled = self.inboxStore.globalFilter.isFilterEnabled(
        GlobalFilterTypes.Snoozed
      );
      const assigneeFilter = self.inboxStore.globalFilter.getFilter(GlobalFilterTypes.Assignee) as
        | IFilterAssignee
        | undefined;
      const anyThreadClosedFilter = self.inboxStore.globalFilter.getFilter(
        GlobalFilterTypes.AnyThreadClosedAt
      ) as IFilterAnyThreadClosed | undefined;
      const sorting = self.inboxStore.selectedSorting;

      return self.spinnerService.getConversations({
        tab: self.id,
        limit: self.pageSize,
        offset: self.offset,
        tags: self.inboxStore.globalFilter.activeTags,
        tagGroupIds: self.inboxStore.globalFilter.activeTagGroupIds,
        onlySnoozed: snoozedFilterEnabled ? true : undefined,
        assigneeId: assigneeFilter?.assigneeId,
        anyThreadClosedAfter: anyThreadClosedFilter?.after,
        anyThreadClosedBefore: anyThreadClosedFilter?.before,
        sorting
      });
    };

    const getAllConversations = async (): Promise<InboxState> => {
      // not *all* actually, just a few
      const limit = config.services.limits.maxConversations;
      const anyThreadClosedFilter = self.inboxStore.globalFilter.getFilter(
        GlobalFilterTypes.AnyThreadClosedAt
      ) as IFilterAnyThreadClosed | undefined;
      const sorting = self.inboxStore.selectedSorting;

      return self.spinnerService.getConversations({
        tab: self.id,
        limit,
        anyThreadClosedAfter: anyThreadClosedFilter?.after,
        anyThreadClosedBefore: anyThreadClosedFilter?.before,
        sorting,
      });
    };

    /**
     * Load next portion of entries.
     */
    const actions = {
      loadNextEntries: flow(function* () {
        if (self.isPermitted) {
          if (self.loadingState === InboxLoadingState.UNLOADED) {
            self.loadedEntries.clear();
          }

          try {
            self.loadingState = InboxLoadingState.PARTIAL_LOADING;
            const { entries, timestamp } = yield getPagedConversations();
            self.offset = timestamp;

            const existingIds = self.loadedEntries.map((entry) => entry.roomId);
            const newEntries = entries
              .filter((entry: SpinnerInboxEntry) => !existingIds.includes(entry.room))
              .map((entry: SpinnerInboxEntry) => conversationResourceDataToSnapshot(entry));

            self.loadedEntries.push(...newEntries);
            newEntries.forEach((entry: IInboxEntry) =>
              self.eventListener.addRoomListener(entry.id, entry)
            );

            self.loadingState =
              self.offset === 0 ? InboxLoadingState.LOADED : InboxLoadingState.LOADED_PARTIALLY;
          } catch (reason) {
            self.loadingState = InboxLoadingState.ERROR;
            throw Error("Could not load next inbox entries");
          }
        }
      }),

      /**
       * Load all filter's entries.
       */
      loadAllEntries: flow(function* () {
        if (self.isPermitted) {
          if (self.loadingState === InboxLoadingState.UNLOADED) {
            self.loadedEntries.clear();
          }

          try {
            self.loadingState = InboxLoadingState.FULL_LOADING;
            const { entries } = yield getAllConversations();

            self.loadedEntries = entries.map((entry: SpinnerInboxEntry) =>
              conversationResourceDataToSnapshot(entry)
            );

            // process entries sequentially
            self.loadedEntries.reduce((acc, entry) =>
              acc.then(() =>
                self.eventListener.addRoomListener(entry.id, entry)
              ),
              Promise.resolve()
            );

            self.loadingState = InboxLoadingState.LOADED;
          } catch (reason) {
            self.loadingState = InboxLoadingState.ERROR;
            logger.error("Error loading inbox", reason);

            throw Error("Could not load inbox entries");
          }
        }
      }),

      /**
       * Pick entry from the collection of loaded entries.
       * The outcome is a record of user activity (self.lastSelectedEntry),
       * not a basis to make the entry visually selected. Higlighting should
       * be based on a loaded room. (See InboxEntry.isSelected)
       * @param {IInboxEntry} entry [description]
       */
      selectEntry(id: string) {
        const entry = self.loadedEntries.find((e: IInboxEntry) => e.id === id) as IInboxEntry;
        if (entry) {
          self.lastSelectedEntry = entry;
          self.routerStore.goToRoom(entry.roomId);
        }
      }
    };

    return actions;
  })
  .actions((self) => ({
    loadEntries: flow(function* () {
      if (self.pageSize > 0) {
        yield self.loadNextEntries();
      } else {
        yield self.loadAllEntries();
      }
    })
  }))
  .actions((self) => ({
    refresh: flow(function* () {
      self.lastSelectedEntry = undefined;
      self.offset = undefined;

      yield self.loadEntries();
    })
  }))
  .actions((self) => {
    const actions = {
      recalculateUnreadEntries() {
        self.setUnreadCount(
          self.inboxStore.globalFilter.getFilteredEntries(self.loadedEntries).length
        );
      },
      recalculateUnreadMessages() {
        const unreadCount = sum(
          self.inboxStore.globalFilter
            .getFilteredEntries(self.loadedEntries)
            .map((e) => e.unreadCount)
        );
        self.setUnreadCount(unreadCount);
      },

      recalculateUnreads() {
        if (!self.unreadType) {
          return;
        }

        if (self.unreadType === UnreadType.ENTRIES) {
          if (self.requiresReload) {
            self.refresh().then(actions.recalculateUnreadEntries);
          } else {
            actions.recalculateUnreadEntries();
          }
        } else if (self.unreadType === UnreadType.MESSAGES) {
          actions.recalculateUnreadMessages();
        }
      }
    };

    return actions;
  })
  .actions((self) => ({
    /**
     * Insert a new entry (eg. from a new message event) into a collection
     * of already loaded entries.
     * XXX: note that sorting entries is performed in Inbox.displayedEntries.
     * @param {IConversationSnapshotIn} snapshot snapshot
     */
    insertEntry(snapshot: IConversationSnapshotIn) {
      if (self.requiresReload && self.id === InboxFilterId.CLOSED) {
        return;
      }

      const entry = InboxEntry.create(snapshot);
      self.loadedEntries.push(entry);
      self.eventListener.addRoomListener(entry.id, entry);

      const { callStore, userActivityService, ringtoneService } = self;
      if (
        self.id === InboxFilterId.YOURS &&
        (!callStore || callStore.isNoCall) &&
        userActivityService.isPageInactive()
      ) {
        ringtoneService.playMessageSoundForInactiveTab();
      }

      if (self.unreadType === UnreadType.MESSAGES) {
        self.recalculateUnreadMessages();
      }
    }
  }))
  .actions((self) => {
    const actions = {
      clearEntries() {
        self.lastSelectedEntry = undefined;
        self.offset = undefined;
        self.loadedEntries.clear();
        self.loadingState = InboxLoadingState.UNLOADED;
      },

      handleGlobalFilterChanged(forceRefresh: boolean) {
        if ((forceRefresh || self.pageSize > 0) && self.isSelected) {
          self.refresh().then(self.recalculateUnreads);
        } else {
          self.recalculateUnreads();
        }
      }
    };

    return actions;
  });

export interface IInboxFilter extends Instance<typeof InboxFilter> { }

/**
 * Determine which filter(s) the message should go into. If the message belongs to more than one tab the order
 * is strictly defined: ["Yours" > "Followed" > "Waiting" > "In Progress"].
 * @param  {SnapshotOrInstance<typeof InboxEntry>} entry InboxEntry node or compatible snapshot
 * @param  {string | undefined} currentUserId current user's id
 * @return {InboxFilterId[]} array of ids of filters the message belongs to
 */
export function determineInboxEntryFilters(
  entry: SnapshotOrInstance<typeof Conversation>,
  currentUserId?: string,
  currentUserSkills?: string[]
): Array<InboxFilterId> {
  if (isStateTreeNode(entry)) {
    entry = getSnapshot(entry);
  }
  let filterIds = [];
  if ([ConversationStatus.Solved, ConversationStatus.Unsolved].includes(entry.status)) {
    // closed, end of story
    filterIds.push(InboxFilterId.CLOSED);
    return filterIds;
  } else {
    if (entry.assignee === currentUserId) {
      filterIds.push(InboxFilterId.YOURS);
    } else if (entry.followers && currentUserId && entry.followers.includes(currentUserId)) {
      filterIds.push(InboxFilterId.FOLLOWED);
    }
    // not responded yet or no assignee
    if (!entry.lastAdviserTimestamp || !entry.assignee) {
      filterIds.push(InboxFilterId.WAITING);
    }
    if (entry.status === ConversationStatus.InProgress) {
      filterIds.push(InboxFilterId.IN_PROGRESS);
    }
  }
  return filterIds;
}
